<template>
  <!-- Audio Input -->
  <div v-show="!isSafari && !isMobile && speechSupport" id="audio-input">
    <div class="w-full mb-2">
      <div id="info-container" class="mt-2 space-y-2">
        <div id="info-no-speech" class="hidden p-4 rounded-md bg-yellow-50">
          <div class="flex">
            <div class="flex-shrink-0">
              <ExclamationIcon class="w-5 h-5 text-yellow-400"/>
            </div>
            <div class="ml-3">
              <p class="text-sm text-yellow-700">
                No speech was detected. You may need to adjust your
                <a class="underline" href="//support.google.com/chrome/bin/answer.py?hl=en&amp;answer=1407892"> microphone settings</a>
              </p>
            </div>
          </div>
        </div>

        <div id="info-allow" class="hidden p-4 rounded-md bg-yellow-50">
          <div class="flex">
            <div class="flex-shrink-0">
              <CursorClickIcon class="w-5 h-5 text-yellow-400" />
            </div>
            <div class="flex-1 ml-3 md:flex md:justify-between">
              <p class="text-sm text-yellow-700">Click the "Allow" button to enable your microphone.</p>
            </div>
          </div>
        </div>

        <div id="info-denied" class="hidden p-4 rounded-md bg-red-50">
          <div class="flex">
            <div class="flex-shrink-0">
              <ExclamationIcon class="w-5 h-5 text-red-400" />
            </div>
            <div class="flex-1 ml-3 md:flex md:justify-between">
              <p class="text-sm text-red-700">Permission to use microphone was denied. Please type your answer.</p>
            </div>
          </div>
        </div>

        <div id="info-blocked" class="hidden p-4 rounded-md bg-red-50">
          <div class="flex">
            <div class="flex-shrink-0">
              <ExclamationIcon class="w-5 h-5 text-red-400" />
            </div>
            <div class="flex-1 ml-3 md:flex md:justify-between">
              <p class="text-sm text-red-700">Permission to use microphone is blocked. To change, go to chrome://settings/contentExceptions#media-stream</p>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6">
        <div class="flex justify-center">
          <button @click="speechToText" type="button" class="flex items-center p-4 border-2 border-transparent rounded-full hover:border-red-500 hover:border-2 w-14 h-14 sm:w-20 sm:h-20 sm:p-4 focus:outline-none" :class="{ 'bg-red-500 animate-pulse text-white': recognizing, 'bg-slate-300': !recognizing }">
            <MicrophoneIcon class="w-6 h-6 sm:w-12 sm:h-12" />
          </button>
        </div>
        <div class="block w-full mt-3 text-xs text-center text-gray-500" v-text="recognizing ? 'Speak now. Click to stop.' : 'Click to speak'"></div>
      </div>
    </div>

    <div id="interim-container" class="relative h-8 p-2 mb-1 overflow-scroll text-xs bg-gray-100 rounded dark:bg-gray-800 whitespace-nowrap no-scrollbar" :class="runtimeTranscription ? '' : 'invisible'">
      <p class="absolute text-gray-500 dark:text-gray-200">
        {{ runtimeTranscription }}
      </p>
    </div>
  </div>

  <Form @submit="onSubmit">
    <Field name="submittedAnswer" v-model="transcription" v-slot="{ field }" rules="required">
      <textarea id="textarea" rows="5" autocomplete="off" class="block w-full border rounded-md shadow-sm placeholder:text-sm focus:ring-slate-300 focus:border-slate-500 sm:text-sm border-slate-300 dark:bg-gray-900 dark:text-white" name="submittedAnswer" :placeholder="isMobile ? 'Tip: Use dictation from your mobile keyboard' : 'Submit anything you can remember; try explaining it to a 12 year old.'" x-webkit-speech v-bind="field"></textarea>
    </Field>
    <error-message class="mt-1 text-sm text-red-500" name="submittedAnswer"></error-message>

    <TaskSubmitButton :cta="'Submit'" :type="'textLong'" :correct="answer.correct" @skip="$emit('skip', $event)" />
  </Form>
</template>

<script>
window.SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const recognition = window.SpeechRecognition ? new window.SpeechRecognition() : false;

import { Form, Field, ErrorMessage, defineRule } from "vee-validate";
import TaskSubmitButton from "../task/TaskSubmitButton.vue";
import { MicrophoneIcon } from "@heroicons/vue/outline";
import { CursorClickIcon, ExclamationIcon } from "@heroicons/vue/solid";
import { ref } from "vue";

defineRule("required", (value) => {
  if (!value) {
    return `No answer? Try your best.`;
  }
  return true;
});

export default {
  components: {
    Form,
    Field,
    ErrorMessage,
    TaskSubmitButton,
    MicrophoneIcon,
    CursorClickIcon,
    ExclamationIcon,
  },
  emits: ["answer", "skip"],

  setup(props, { emit }) {
    const answer = ref({
      item: null,
      correct: null,
    });
    const runtimeTranscription = ref("");
    const transcription = ref("");
    const lang = ref("en-US");
    const recognizing = ref(false);
    const startTimestamp = ref("");
    const ignoreOnend = ref(Boolean);
    const isSafari = ref(window.safari !== undefined);
    const isMobile = ref(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
    const speechSupport = ref(!recognition ? false : true);

    function onSubmit(e) {
      answer.value = {
        item: e.submittedAnswer,
        correct: true,
      };

      emit("answer", answer.value);
    }

    function showInfo(elementId) {
      const alert = document.getElementById(elementId);
      if (alert) {
        if (alert.classList.contains("hidden")) {
          alert.classList.remove("hidden");
        }
      }
    }

    function hideInfo(elementId) {
      const alert = document.getElementById(elementId);
      if (alert) {
        if (!alert.classList.contains("hidden")) {
          alert.classList.add("hidden");
        }
      }
    }

    function capitalize(s) {
      var firstChar = /\S/;
      return s.replace(firstChar, function (m) {
        return m.toUpperCase();
      });
    }

    async function speechToText(event) {
      if (recognizing.value) {
        recognition.stop();
        return;
      }

      // Not supported in safari
      const permission = await navigator.permissions.query({ name: "microphone" });
      if (permission.state !== "granted") {
        showInfo("info-allow");
      }

      recognition.lang = lang.value;
      recognition.interimResults = true;
      recognition.continuous = true;
      startTimestamp.value = event.timeStamp;
      ignoreOnend.value = false;

      recognition.onstart = () => {
        recognizing.value = true;
        hideInfo("info-allow");
      };

      recognition.onresult = (event) => {
        runtimeTranscription.value = "";
        for (var i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            transcription.value += event.results[i][0].transcript;
          } else {
            runtimeTranscription.value += event.results[i][0].transcript;
          }
        }

        transcription.value = capitalize(transcription.value);

        // keep scrolling to the right for interim transcript
        const interimContainer = document.getElementById("interim-container");
        interimContainer.scrollLeft = interimContainer.scrollWidth - interimContainer.clientWidth;

        // keep scrolling to bottom of textarea
        const textarea = document.getElementById("textarea");
        textarea.scrollTop = textarea.scrollHeight;
      };
      // end of transcription

      recognition.onend = () => {
        recognizing.value = false;
        if (ignoreOnend.value) {
          return;
        }
      };

      recognition.onerror = (event) => {
        console.log(event);

        if (event.error == "no-speech") {
          showInfo("info-no-speech");
          ignoreOnend.value = true;
        }
        if (event.error == "audio-capture") {
          showInfo("info-no-microphone");
          ignoreOnend.value = true;
        }
        if (event.error == "not-allowed") {
          if (event.timeStamp - startTimestamp.value < 10) {
            showInfo("info-blocked");
          } else {
            showInfo("info-denied");
          }
          ignoreOnend.value = true;
        }

        hideInfo("info-allow");
      };

      recognition.start();
    }

    return {
      answer,
      runtimeTranscription,
      transcription,
      lang,
      recognizing,
      startTimestamp,
      ignoreOnend,
      isSafari,
      isMobile,
      speechSupport,
      onSubmit,
      speechToText,
    };
  },
};
</script>
